import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useState } from 'react';
import { twMerge } from '@/lib/twMerge';
import InitialsIcon from '@/components/InitialsIcon';
import Popover from '@/components/Popover';
import { useCreditNudge } from '@/components/TopBar/CreditNudge';
import SwitchToAnnual from '@/components/TopBar/SwitchToAnnual';
import { useSubscriptionType } from '@/hooks/auth';
import { useAccountDetails } from '@/hooks/billing/useAccountDetails';
import { useSettings } from '@/hooks/state/useSettings';
import { useLogout } from '@/hooks/useLogout';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { SidebarContext } from '@/providers/SidebarProvider';
import { Button, Text } from '@music-vine/cadence/ui';
import { useEnableAutoRenew } from '@/hooks/billing/useEnableAutoRenew';
import { useSubEndDate } from '@/hooks/billing/useSubEndDate';
import { OverduePaymentIcon } from './OverduePaymentIcon';
import { OverduePaymentNotice } from './OverduePaymentNotice';
import { RemainingCredits } from './RemainingCredits';
import { UserInfo } from './UserInfo';
import { MenuLink, UserMenuNav } from './UserMenuNav';
const UserImage = ({
  className,
  children
}: {
  className?: string;
  children?: React.ReactNode;
}) => {
  const {
    settings
  } = useSettings();
  const profilePicture = settings?.user?.profile_picture;
  const fullName = [settings?.user?.first_name, settings?.user?.last_name].filter(Boolean).join(' ');
  return <>
      {profilePicture ? <Image alt={fullName} className={twMerge('aspect-square size-full shrink-0 rounded-full', className)} height={40} sizes="40px" src={profilePicture} width={40} /> : <InitialsIcon className={className} />}
      {children}
    </>;
};
const SubscriptionCancelledNotice = () => {
  const {
    enableAutoRenew,
    isUpdating
  } = useEnableAutoRenew();
  const subEndDate = useSubEndDate();
  const subscriptionType = useSubscriptionType();
  if (!subEndDate) {
    return null;
  }
  return <div className="w-full shrink-0 bg-red-50 px-6 py-5 dark:bg-red-950" data-sentry-component="SubscriptionCancelledNotice" data-sentry-source-file="index.tsx">
      <div className="flex flex-col items-start gap-4">
        <Text className="text-balance text-base text-gray-950" data-sentry-element="Text" data-sentry-source-file="index.tsx">
          Your {subscriptionType} subscription ends on{' '}
          <strong className="font-semibold">{subEndDate}</strong>.
        </Text>
        <Button disabled={isUpdating} onClick={enableAutoRenew} size="sm" variant="contrast" data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {isUpdating ? 'Reactivating...' : 'Reactivate'}
        </Button>
      </div>
    </div>;
};
const UserMenu = () => {
  const {
    logout
  } = useLogout();
  const router = useRouter();
  const accountType = useSubscriptionType();
  const {
    data: accountDetails
  } = useAccountDetails();
  const subEndDate = useSubEndDate();
  const {
    isMd
  } = useTWScreens();
  const {
    nudgeTooltip
  } = useCreditNudge();
  const {
    toggleSidebar
  } = useContext(SidebarContext);

  // Close the mobile nav menu when the account menu opens so it isn't left
  // covering the page underneath (DEV-6693)
  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      toggleSidebar(false, 'mobile');
    }
  }, [toggleSidebar]);

  // Disable tooltip briefly after navigation to prevent it showing
  // when mouse happens to be over the button area on page load
  const [tooltipDisabled, setTooltipDisabled] = useState(true);
  useEffect(() => {
    setTooltipDisabled(true);
    const timeout = setTimeout(() => {
      setTooltipDisabled(false);
    }, 500);
    return () => clearTimeout(timeout);
  }, [router.asPath]);
  const accountMenuTooltip = tooltipDisabled ? undefined : {
    label: 'Account menu'
  };
  const showNudgeOnUserIcon = !isMd && nudgeTooltip;
  const triggerTooltip = showNudgeOnUserIcon ? nudgeTooltip : accountMenuTooltip;
  return <Popover.Container animateOverTrigger className="flex flex-1 flex-col p-0 md:w-72 md:max-w-[calc(100vw-2rem)]" handleColor="dark" onOpenChange={handleOpenChange} data-sentry-element="Popover.Container" data-sentry-component="UserMenu" data-sentry-source-file="index.tsx">
      <Popover.Trigger tooltip={triggerTooltip} data-sentry-element="Popover.Trigger" data-sentry-source-file="index.tsx">
        <Button className="flex aspect-square items-center justify-center overflow-hidden rounded-full bg-white ring-pink-500 transition-shadow hover:brightness-95 focus-visible:shadow-pink-500 focus-visible:ring-1 dark:bg-gray-950" fontSize="sm" icon size={{
        default: 'sm',
        lg: 'default',
        xl: 'lg'
      }} type="button" variant="transparent" data-sentry-element="Button" data-sentry-source-file="index.tsx">
          <UserImage className="size-full p-0" data-sentry-element="UserImage" data-sentry-source-file="index.tsx">
            <OverduePaymentIcon data-sentry-element="OverduePaymentIcon" data-sentry-source-file="index.tsx" />
          </UserImage>
        </Button>
      </Popover.Trigger>
      <Popover.Content data-sentry-element="Popover.Content" data-sentry-source-file="index.tsx">
        <div className="scrollbar-thin relative flex h-full max-h-[90dvh] flex-col overflow-y-auto max-xs:pb-12">
          <div className="flex flex-col border-0">
            <div>
              <div className={clsx('sticky top-0 flex flex-col gap-5 p-5 text-gray-950 max-xs:rounded-t-xl xl:px-6 dark:bg-gray-900', accountType === 'Free' ? 'bg-gray-50' : 'bg-pink-50 dark:bg-pink-950')}>
                <UserInfo data-sentry-element="UserInfo" data-sentry-source-file="index.tsx" />
                {accountType === 'Free' && <RemainingCredits />}
              </div>
              <OverduePaymentNotice accountType={accountType} data-sentry-element="OverduePaymentNotice" data-sentry-source-file="index.tsx" />
            </div>
            <UserMenuNav data-sentry-element="UserMenuNav" data-sentry-source-file="index.tsx" />
          </div>
          <div className="m-3 mt-1 flex flex-col border-0 border-gray-150 border-t-1 border-solid pt-4 max-xl:mx-4 md:mt-0 md:pt-3 dark:border-gray-800">
            <MenuLink onClick={() => logout()} data-sentry-element="MenuLink" data-sentry-source-file="index.tsx">Log out</MenuLink>
          </div>
          {accountType !== 'Free' && !subEndDate && accountDetails?.form?.subscription?.billing_cycle?.toLowerCase() !== 'annually' && <SwitchToAnnual />}
          <SubscriptionCancelledNotice data-sentry-element="SubscriptionCancelledNotice" data-sentry-source-file="index.tsx" />
        </div>
      </Popover.Content>
    </Popover.Container>;
};
export default UserMenu;